import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddIcon from '@mui/icons-material/Add';
import CopyAll from '@mui/icons-material/CopyAll';
import Box from '@mui/material/Box';
import WizardLayout from './WizardLayout';
import BatchDetailsAccordion from './BatchDetailsAccordion';
import ShipperAllocationAccordion from './ShipperAllocationAccordion';
import { useCreateBatchMutation } from '../batchesEndpoints';

const Review = ({ onClose, onComplete, onCopy, onPrev, onReset, batchState }) => {
  const [submit, { data, isSuccess, isUninitialized, isError, error }] = useCreateBatchMutation();

  // prettier-ignore
  // batchOwnerToAffiliate :: BatchOwner -> Affiliate
  const batchOwnerToAffiliate = ({ 
  dataCompanyId, 
  customerIdName: companyCustomerIdName,
 }) => ({
  companyCustomerIdName,
  dataCompanyId: parseInt(dataCompanyId),
});

  const isGasoline = R.propEq('isGasoline', true, batchState.product);

  // shipperToAffiliate :: Shipper -> Affiliate
  const shipperToAffiliate =
    batchOwner =>
    ({ shipperIdName: companyCustomerIdName }) => ({
      companyCustomerIdName,
      dataCompanyId: parseInt(batchOwner.dataCompanyId),
    });

  const shipmentMap = ({
    bookingOrigin: bookingOriginId,
    location: locationId,
    ...shipmentRest
  }) => ({
    ...shipmentRest,
    bookingOriginId,
    locationId,
  });

  const shipmentTransformations = {
    bookingOrigin: R.prop('bookingOriginId'),
    location: R.prop('id'),
    shipper: shipperToAffiliate(batchState.batchOwner),
    volume: R.compose(parseInt, R.replace(/\D/g, '')),
  };

  // prettier-ignore
  const transformations = {
  batchOwner: batchOwnerToAffiliate,
  component: isGasoline ? R.prop('data') : R.always(null),
  fuelType: R.prop('data'),
  origin: R.prop('originId'),
    oxygenate: isGasoline ? R.identity : R.always(null),
  product: R.prop('productId'),
  rvp: isGasoline ? R.prop('data') : R.always(null),
  shipments: R.compose(
    R.map(shipmentMap), 
    R.map(R.evolve(shipmentTransformations))
  ),
  sulphurContent: isGasoline ? R.always(null) : R.prop('data'),
};

  const transformState = R.compose(
    ({ origin: originId, product: productId, ...loadRest }) => ({
      ...loadRest,
      originId,
      productId,
    }),
    R.evolve(transformations)
  );

  const handleSubmit = () => {
    submit({ body: transformState(batchState) });
  };

  const buttons = (
    <>
      {isSuccess ? (
        <Button onClick={onReset} startIcon={<AddIcon />} sx={{ width: 130 }} variant="outlined">
          New Batch
        </Button>
      ) : (
        <Button onClick={onPrev} sx={{ width: 104 }} variant="outlined">
          Back
        </Button>
      )}

      {isSuccess || isError ? (
        <Box display="flex" gap={1.2} sx={{ marginLeft: '10px' }}>
          <Button
            disabled={!isSuccess}
            onClick={onCopy}
            startIcon={<CopyAll />}
            sx={{ width: 130 }}
            variant="outlined"
          >
            Copy Batch
          </Button>
          <Button onClick={onComplete} sx={{ width: 104 }}>
            Close
          </Button>
        </Box>
      ) : (
        <Button
          loading={!isUninitialized && !data}
          loadingPosition="end"
          onClick={handleSubmit}
          sx={{ width: 104 }}
          variant="contained"
        >
          Submit
        </Button>
      )}
    </>
  );

  return (
    <WizardLayout buttons={buttons} onClose={R.or(isSuccess, isError) ? onComplete : onClose}>
      {isSuccess && (
        <Stack sx={{ alignItems: 'center', pb: 7, pt: 5 }}>
          <CheckRoundedIcon sx={{ color: 'primary.main', fontSize: 70 }} />
          <Typography sx={{ color: 'text.primary', mb: 2 }} variant="h5">
            Your Batch was Successfully Submitted!
          </Typography>
          <Typography sx={{ textAlign: 'center' }} variant="body1">
            {data.message}
          </Typography>
          <Typography sx={{ textAlign: 'center' }} variant="body1">
            Batch Details
          </Typography>
        </Stack>
      )}

      {isError && (
        <Stack sx={{ alignItems: 'center', pb: 7, pt: 5 }}>
          <CloseRoundedIcon sx={{ color: 'error.main', fontSize: 70 }} />
          <Typography sx={{ color: 'text.primary', mb: 2 }} variant="h5">
            Your Batch Could Not be Created.
          </Typography>
          <Typography sx={{ textAlign: 'center' }} variant="body1">
            {error.data.title}
          </Typography>
        </Stack>
      )}

      <Stack spacing={4}>
        <BatchDetailsAccordion batchData={data} batchState={batchState} />
        <ShipperAllocationAccordion batchData={data} batchState={batchState} />
      </Stack>
    </WizardLayout>
  );
};

Review.propTypes = {
  batchState: PropTypes.shape({
    batchOwner: PropTypes.shape({}),
    product: PropTypes.shape({}),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default Review;
